import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@/i18n/I18nContext';
import { LanguageSelector } from '@/components/LanguageSelector';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, MessageSquare, Globe, Zap, Shield } from 'lucide-react';

export default function Landing() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const features = [
    {
      icon: <MessageSquare className="w-6 h-6 text-emerald-600" />,
      title: t('landing.feature1Title'),
      description: t('landing.feature1Desc'),
    },
    {
      icon: <Globe className="w-6 h-6 text-emerald-600" />,
      title: t('landing.feature2Title'),
      description: t('landing.feature2Desc'),
    },
    {
      icon: <Zap className="w-6 h-6 text-emerald-600" />,
      title: t('landing.feature3Title'),
      description: t('landing.feature3Desc'),
    },
    {
      icon: <Shield className="w-6 h-6 text-emerald-600" />,
      title: t('landing.feature4Title'),
      description: t('landing.feature4Desc'),
    },
  ];

  const bullets = [
    t('landing.bullet1'),
    t('landing.bullet2'),
    t('landing.bullet3'),
    t('landing.bullet4'),
    t('landing.bullet5'),
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white" data-testid="landing-header">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <MessageSquare className="w-8 h-8 text-emerald-600" />
            <span className="text-2xl font-bold text-slate-900">EVA AI</span>
          </div>
          <div className="flex items-center space-x-3">
            <LanguageSelector />
            <Button
              variant="ghost"
              onClick={() => navigate('/login')}
              data-testid="button-login"
            >
              {t('landing.login')}
            </Button>
            <Button
              onClick={() => navigate('/signup')}
              data-testid="button-signup"
            >
              {t('landing.startNow')}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="max-w-4xl mx-auto px-6 py-24 text-center" data-testid="hero-section">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
          {t('landing.heroTitle1')}{' '}
          <br className="hidden sm:block" />
          <span className="text-emerald-600">{t('landing.heroTitle2')}</span>
        </h1>
        <p className="text-base sm:text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
          {t('landing.heroSubtitle')}
        </p>
        <div className="flex items-center justify-center space-x-4">
          <Button
            size="lg"
            onClick={() => navigate('/signup')}
            data-testid="button-hero-cta"
          >
            {t('landing.heroCta')}
          </Button>
          <Button
            size="lg"
            variant="outline"
            onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
            data-testid="button-learn-more"
          >
            {t('landing.learnMore')}
          </Button>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-16" data-testid="features-section">
        <div className="text-center mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">{t('landing.featuresTitle')}</h2>
          <p className="text-base sm:text-lg text-slate-600">{t('landing.featuresSubtitle')}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <Card key={index} data-testid={`feature-card-${index}`}>
              <CardHeader>
                <div className="mb-4">{feature.icon}</div>
                <CardTitle className="text-lg">{feature.title}</CardTitle>
                <CardDescription>{feature.description}</CardDescription>
              </CardHeader>
            </Card>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="max-w-4xl mx-auto px-6 py-16" data-testid="pricing-section">
        <div className="text-center mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">{t('landing.pricingTitle')}</h2>
          <p className="text-base sm:text-lg text-slate-600">{t('landing.pricingSubtitle')}</p>
        </div>
        <Card className="max-w-md mx-auto border-emerald-200">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl mb-2">{t('landing.planName')}</CardTitle>
            <div className="text-5xl font-bold text-emerald-600 mb-2">99 CHF</div>
            <CardDescription className="text-base">{t('landing.planPeriod')}</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3 mb-6">
              {bullets.map((text, i) => (
                <li key={i} className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-emerald-600 mr-3 flex-shrink-0" />
                  <span className="text-sm">{text}</span>
                </li>
              ))}
            </ul>
            <Button
              className="w-full"
              size="lg"
              onClick={() => navigate('/signup')}
              data-testid="button-pricing-cta"
            >
              {t('landing.startNow')}
            </Button>
          </CardContent>
        </Card>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <MessageSquare className="w-6 h-6 text-emerald-600" />
                <div>
                  <span className="text-lg font-semibold text-slate-900">EVA AI</span>
                  <p className="text-xs text-slate-500">
                    Titolare del servizio: <a href="/terms" className="hover:text-emerald-600" data-testid="link-operator">Giuseppe Martini</a>
                  </p>
                </div>
            </div>
            <div className="flex items-center space-x-6 text-sm text-slate-600">
              <a href="/privacy" className="hover:text-emerald-600" data-testid="link-privacy">{t('landing.privacy')}</a>
              <a href="/terms" className="hover:text-emerald-600" data-testid="link-terms">{t('landing.terms')}</a>
              <a href="/dpa" className="hover:text-emerald-600" data-testid="link-dpa">{t('landing.dpa')}</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
